import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { date: "", name: "", type: "national" };

const fmtDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "long", year: "numeric",
  });

export default function PortalHolidays() {
  usePageMeta("Holidays — SKN Group Portal", "Company holiday calendar.");
  const { user } = useAuth();
  const isManager = user?.is_manager;

  const [holidays, setHolidays] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/holidays`, { withCredentials: true });
      setHolidays(res.data);
    } catch (err) {
      console.error("Failed to load holidays", err);
      setHolidays([]);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({ date: h.date, name: h.name, type: h.type });
    setOpen(true);
  };

  const save = async () => {
    if (!form.date || !form.name.trim()) {
      toast.error("Date and name are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/holidays/${editing.id}`, form, { withCredentials: true });
        toast.success("Holiday updated.");
      } else {
        await axios.post(`${API}/holidays`, form, { withCredentials: true });
        toast.success("Holiday added.");
      }
      setOpen(false);
      fetchHolidays();
    } catch (err) {
      console.error("Failed to save holiday", err);
      toast.error("Could not save the holiday.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/holidays/${id}`, { withCredentials: true });
      toast.success("Holiday deleted.");
      fetchHolidays();
    } catch (err) {
      console.error("Failed to delete holiday", err);
      toast.error("Could not delete the holiday.");
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="portal-holidays-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link to="/portal" data-testid="holidays-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#B8997A] transition-colors">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Portal
        </Link>
        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
            Holiday <span className="italic bronze">Calendar</span>
          </h1>
          {isManager && (
            <button
              onClick={openCreate}
              data-testid="holiday-add-button"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-foreground/25 px-4 py-2.5 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors self-start"
            >
              <Plus size={13} strokeWidth={1.5} /> Add Holiday
            </button>
          )}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Indian national and West Bengal public holidays. Upcoming dates are highlighted.
        </p>

        {holidays === null ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin bronze" />
          </div>
        ) : (
          <div className="mt-10 space-y-px bg-border border border-foreground/10" data-testid="holidays-list">
            {holidays.map((h) => {
              const upcoming = h.date >= today;
              return (
                <div
                  key={h.id}
                  data-testid={`holiday-item-${h.id}`}
                  className={`flex items-center justify-between gap-4 p-5 ${
                    upcoming ? "bg-background border-l-2 border-[#B8997A]" : "bg-background opacity-55"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <CalendarDays size={18} strokeWidth={1.25} className={upcoming ? "bronze" : "text-muted-foreground"} />
                    <div>
                      <p className="font-medium text-sm md:text-base">{h.name}</p>
                      <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{fmtDate(h.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge
                      variant="outline"
                      className={`uppercase tracking-wider text-[10px] rounded-none ${
                        h.type === "national" ? "border-[#0B0F14]/40 text-[#0B0F14]" : "border-[#B8997A] text-[#8a6f52]"
                      }`}
                    >
                      {h.type}
                    </Badge>
                    {isManager && (
                      <>
                        <button
                          onClick={() => openEdit(h)}
                          data-testid={`holiday-edit-${h.id}`}
                          aria-label="Edit holiday"
                          className="p-2 border border-foreground/15 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors"
                        >
                          <Pencil size={12} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => remove(h.id)}
                          data-testid={`holiday-delete-${h.id}`}
                          aria-label="Delete holiday"
                          className="p-2 border border-foreground/15 hover:border-destructive hover:text-destructive transition-colors"
                        >
                          <Trash2 size={12} strokeWidth={1.5} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-[#F7F5F0] max-w-md" data-testid="holiday-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {editing ? "Edit Holiday" : "Add Holiday"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Date</Label>
                <Input
                  type="date"
                  data-testid="holiday-input-date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="bg-transparent border-foreground/25"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Name</Label>
                <Input
                  data-testid="holiday-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Rabindra Jayanti"
                  className="bg-transparent border-foreground/25"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="holiday-select-type" className="bg-transparent border-foreground/25">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F7F5F0]">
                    <SelectItem value="national" data-testid="holiday-type-national">National</SelectItem>
                    <SelectItem value="regional" data-testid="holiday-type-regional">Regional (West Bengal)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={save}
                disabled={saving}
                data-testid="holiday-save-button"
                className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {editing ? "Save Changes" : "Add Holiday"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
